import { parseMimeType } from '@scalar/helpers/http/mime-type';
import { escapeSingleQuotes } from '../../../libs/shell.js';
/**
 * True for `application/json`, any RFC 6839 `+json` structured-syntax suffix
 * (e.g. `application/vnd.api+json`), and parameterized variants
 * (e.g. `application/json;charset=utf-8`). Case-insensitive.
 */
const isJsonContentType = (value) => {
    if (!value) {
        return false;
    }
    const { subtype } = parseMimeType(value);
    return subtype === 'json' || subtype.endsWith('+json');
};
/**
 * shell/curl
 */
export const shellCurl = {
    target: 'shell',
    client: 'curl',
    title: 'Curl',
    generate(request, configuration) {
        // Defaults
        const normalizedRequest = {
            method: 'GET',
            ...request,
        };
        // Normalization
        normalizedRequest.method = normalizedRequest.method.toUpperCase();
        // Build curl command parts
        const parts = ['curl'];
        // Build the URL, joining extra query parameters with `&` when the URL already carries a query string
        const baseUrl = normalizedRequest.url ?? '';
        const separator = baseUrl.includes('?') ? '&' : '?';
        const queryString = normalizedRequest.queryString?.length
            ? separator +
                normalizedRequest.queryString
                    .map((param) => {
                    // Ensure both name and value are fully URI encoded
                    return `${param.name}=${param.value}`;
                })
                    .join('&')
            : '';
        const url = `${baseUrl}${queryString}`;
        // Quote the URL whenever it contains anything the shell could interpret (spaces, query separators, globs, …)
        const isShellSafe = /^[A-Za-z0-9._~:/%@+,=-]*$/.test(url);
        const urlPart = isShellSafe ? url : `'${escapeSingleQuotes(url)}'`;
        parts[0] = `curl ${urlPart}`;
        // Method
        if (normalizedRequest.method !== 'GET') {
            parts.push(`--request ${normalizedRequest.method}`);
        }
        // Basic Auth
        if (configuration?.auth?.username && configuration?.auth?.password) {
            const authValue = escapeSingleQuotes(`${configuration.auth.username}:${configuration.auth.password}`);
            parts.push(`--user '${authValue}'`);
        }
        // Headers
        if (normalizedRequest.headers?.length) {
            normalizedRequest.headers.forEach((header) => {
                const headerValue = escapeSingleQuotes(`${header.name}: ${header.value}`);
                parts.push(`--header '${headerValue}'`);
            });
            // Add compressed flag if Accept-Encoding header includes compression
            const acceptEncoding = normalizedRequest.headers.find((header) => header.name.toLowerCase() === 'accept-encoding');
            if (acceptEncoding && /gzip|deflate/.test(acceptEncoding.value)) {
                parts.push('--compressed');
            }
        }
        // Cookies
        if (normalizedRequest.cookies?.length) {
            const cookieString = normalizedRequest.cookies
                .map((cookie) => {
                // Encode both cookie name and value to handle special characters
                const encodedName = encodeURIComponent(cookie.name);
                const encodedValue = encodeURIComponent(cookie.value);
                return `${encodedName}=${encodedValue}`;
            })
                .join('; ');
            const escapedCookieString = escapeSingleQuotes(cookieString);
            parts.push(`--cookie '${escapedCookieString}'`);
        }
        // Body
        if (normalizedRequest.postData) {
            if (isJsonContentType(normalizedRequest.postData.mimeType)) {
                // Pretty print JSON data
                if (normalizedRequest.postData.text) {
                    try {
                        const jsonData = JSON.parse(normalizedRequest.postData.text);
                        const prettyJson = JSON.stringify(jsonData, null, 2);
                        const escapedJson = escapeSingleQuotes(prettyJson);
                        parts.push(`--data '${escapedJson}'`);
                    }
                    catch {
                        // If JSON parsing fails, use the original text
                        const escapedText = escapeSingleQuotes(normalizedRequest.postData.text ?? '');
                        parts.push(`--data '${escapedText}'`);
                    }
                }
            }
            else if (normalizedRequest.postData.mimeType === 'application/octet-stream') {
                const escapedText = escapeSingleQuotes(normalizedRequest.postData.text ?? '');
                parts.push(`--data-binary '${escapedText}'`);
            }
            else if (normalizedRequest.postData.mimeType === 'application/x-www-form-urlencoded' &&
                normalizedRequest.postData.params) {
                // Handle URL-encoded form data
                normalizedRequest.postData.params.forEach((param) => {
                    const escapedValue = escapeSingleQuotes(param.value ?? '');
                    const encodedName = encodeURIComponent(param.name);
                    const escapedName = escapeSingleQuotes(encodedName);
                    parts.push(`--data-urlencode '${escapedName}=${escapedValue}'`);
                });
            }
            else if (normalizedRequest.postData.mimeType === 'multipart/form-data' && normalizedRequest.postData.params) {
                // Handle multipart form data
                normalizedRequest.postData.params.forEach((param) => {
                    const escapedName = escapeSingleQuotes(param.name);
                    const multipartValueSuffix = param.contentType ? `;type=${param.contentType}` : '';
                    if (param.fileName !== undefined) {
                        const escapedFileName = escapeSingleQuotes(`${param.fileName}${multipartValueSuffix}`);
                        parts.push(`--form '${escapedName}=@${escapedFileName}'`);
                    }
                    else {
                        const rawValue = param.value ?? '';
                        // Pretty-print parts whose contentType is JSON so the snippet stays readable,
                        // mirroring what we already do for `--data` JSON bodies above.
                        const isJsonPart = isJsonContentType(param.contentType);
                        let displayValue = rawValue;
                        if (isJsonPart && rawValue) {
                            try {
                                displayValue = JSON.stringify(JSON.parse(rawValue), null, 2);
                            }
                            catch {
                                // Fall back to the raw value if it is not valid JSON.
                            }
                        }
                        const escapedValue = escapeSingleQuotes(`${displayValue}${multipartValueSuffix}`);
                        parts.push(`--form '${escapedName}=${escapedValue}'`);
                    }
                });
            }
            else {
                // Try to parse and pretty print if it's JSON, otherwise use raw text
                try {
                    const jsonData = JSON.parse(normalizedRequest.postData.text ?? '');
                    const prettyJson = JSON.stringify(jsonData, null, 2);
                    const escapedJson = escapeSingleQuotes(prettyJson);
                    parts.push(`--data '${escapedJson}'`);
                }
                catch {
                    const escapedText = escapeSingleQuotes(normalizedRequest.postData.text ?? '');
                    parts.push(`--data '${escapedText}'`);
                }
            }
        }
        return parts.join(' \\\n  ');
    },
};
